import React, { useEffect, useState } from "react";
import i18n from "../../i18n/config";
import { useHistory } from "react-router-dom";
import { ROUTES } from "../../constants/clientRoutes";
import { AllocationType } from "../../types/api/allocations";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../types/redux";
import { Field, reduxForm, getFormValues, InjectedFormProps } from "redux-form";
import { WizardChildProps } from "../../types/props";

import CurrencyInput from "../../components/Common/CurrencyInput";
import { ResetAllocationsToSubmit, SetAllocationsToSubmit } from "../../actions/allocations";
import useErrorModal from "../../hooks/useErrorModal";

const SpecificAmount: React.FC<WizardChildProps & InjectedFormProps<{}, WizardChildProps>> = (props: WizardChildProps) => {
  const { setTitle, setForwardButton } = props;
  const history = useHistory();
  const openErrorModal = useErrorModal();
  const dispatch = useDispatch();
  const selectedAllocations = useSelector((state: RootState) => state.allocations.selectedAllocations) as AllocationType[];
  const formValues = useSelector((state: RootState) => getFormValues("specificAmount")(state)) as { amount: string };
  const [selectedAccount, setSelectedAccount] = useState<string>();
  const initialForwardButtonState = {
    text: i18n.t("COMMON.NEXT"),
    onClick: () => handleNextClick(),
    disabled: true,
    loading: false
  };

  useEffect(() => {
    if (!selectedAllocations.length) {
      history.replace(ROUTES.ALLOCATIONS.INDEX);
      return;
    }

    dispatch(ResetAllocationsToSubmit());
    setTitle(i18n.t("PAGES.ALLOCATIONS.SPECIFIC_AMOUNT.TITLE"));
    setForwardButton(initialForwardButtonState);

  }, []);

  useEffect(() => {
    setForwardButton({
      ...initialForwardButtonState,
      disabled: !selectedAccount || !formValues?.amount
    });
  }, [selectedAccount, formValues]);

  const handleNextClick = () => {
    const mutableAllocations = selectedAllocations.map((allocation: AllocationType) => ({ ...allocation }));
    const selectedAllocation = mutableAllocations.find((allocation: AllocationType) => allocation.id === Number(selectedAccount));

    // selectedAccount comes from the form and selectedAllocations from redux; a mismatch means they are out of sync
    if (!selectedAllocation) {
      openErrorModal();
      return;
    }

    selectedAllocation.percentage = 0;
    selectedAllocation.getsRemainder = false;
    selectedAllocation.fixed = Number(formValues.amount);
    dispatch(SetAllocationsToSubmit([selectedAllocation]));
    history.push(ROUTES.ALLOCATIONS.REMAINING_AMOUNT);
  }

  if (!selectedAllocations.length) {
    return null;
  }

  return (

    <>
      <p className="bold spacing-bottom-medium">{i18n.t("PAGES.ALLOCATIONS.SPECIFIC_AMOUNT.RADIO_BUTTONS_HEADING")}</p>

      <div className="radio-btns allocation">

        {selectedAllocations.map((allocation: AllocationType) => (
          <label htmlFor={String(allocation.id)} key={allocation.id}>
            <span className="sr-only">{allocation.accountName} {allocation.name}</span>
            <input
              type="radio"
              id={String(allocation.id)}
              value={String(allocation.id)}
              name="allocations"
              onChange={(e) => setSelectedAccount(e.target.value)}
            />
            <span className="radio__label">
              <span className="spacing-left-xs">{allocation.accountName}</span>
              <br />
              <span className="italic">{allocation.name}</span>
            </span>
          </label>
        ))}
      </div>

      <div className="max-w-360">
        <label htmlFor="amount">Select amount to deposit to account selected above.</label>
        <Field
          type="number"
          required={true}
          component={CurrencyInput}
          decimalPlaces={2}
          currencyIso={selectedAllocations[0].productCurrency.code}
          disabled={false}
          name="amount"
          aria-describedby="basic-addon2"
        />
      </div>

    </>
  );
}

export default reduxForm<{}, WizardChildProps>({
  form: "specificAmount",
})(SpecificAmount);